import type { CharacterGenerationBatchItem } from '@cartwhl/common/api/getCharacterGenerationsByBatch';

import type { CharacterJobMetadata } from './types';

/** Matches backend generated-character FBX download policy. */
export const CHARACTER_FBX_DOWNLOAD_READY_STATUSES = new Set([
  'COMPLETE',
  'NEEDS_VALIDATION',
]);

export function isCharacterFbxDownloadReady(
  uploadStatus: string | undefined | null,
): boolean {
  if (!uploadStatus) return false;
  return CHARACTER_FBX_DOWNLOAD_READY_STATUSES.has(uploadStatus);
}

export function isCharacterReprocessing(job: CharacterJobMetadata): boolean {
  return (
    job.uploadStatus === 'AUTORIGGING_IN_PROGRESS' ||
    job.uploadStatus === 'RETARGETING_IN_PROGRESS'
  );
}

export function isFailedCharacterJob(job: CharacterJobMetadata) {
  return (
    job.uploadStatus === 'FAILED' ||
    job.uploadStatus === 'ADJUSTMENT_FAILED' ||
    (!isCompleteCharacterJob(job) &&
      job.generatedStatus === '3D_CONVERT_FAILED')
  );
}

export function isCompleteCharacterJob(job: CharacterJobMetadata) {
  return (
    job.uploadStatus === 'COMPLETE' || job.uploadStatus === 'NEEDS_VALIDATION'
  );
}

export function isInFlightCharacterJob(job: CharacterJobMetadata) {
  return !isCompleteCharacterJob(job) && !isFailedCharacterJob(job);
}

/** Rigged deliverable GLB for verification / BVH preview (not raw base.glb). */
export function characterVerificationMeshPath(
  job: CharacterJobMetadata,
): string {
  return job.characterFileURL?.trim() || job.baseGlbURL?.trim() || '';
}

export function characterCanVerify3D(job: CharacterJobMetadata) {
  return (
    isCompleteCharacterJob(job) && Boolean(characterVerificationMeshPath(job))
  );
}

export function characterCanDownloadFbx(job: CharacterJobMetadata) {
  return (
    isCharacterFbxDownloadReady(job.uploadStatus) &&
    !isCharacterReprocessing(job) &&
    Boolean(job.characterFbxURL?.trim())
  );
}

export function characterBatchReprocessingInProgress(
  jobs: CharacterJobMetadata[],
): boolean {
  return jobs.some(isCharacterReprocessing);
}

/** User-facing summary only; retain the original error on the job for diagnostics. */
export function characterJobErrorMessage(job: CharacterJobMetadata): string {
  const error = job.errorMessage ?? '';
  if (error === 'Unable to generate image.') {
    return error;
  }
  if (job.uploadStatus === 'ADJUSTMENT_FAILED') {
    return 'An error occurred during rigging.';
  }
  if (
    /retopolog|remesh|topolog|triangulat|decimat|mesh reduction|[/\\]reduction[/\\]|reduce\.py|runner\.log/i.test(
      error,
    )
  ) {
    return 'An error occurred during topology cleanup.';
  }
  if (/textur|material|normal[_ -]?map|bake\.py|[/\\]bake[/\\]/i.test(error)) {
    return 'An error occurred during texturing.';
  }
  if (
    /auto[_ -]?rig|rigging|rig failed|skinning|skin weights|skeleton|adjust[_ -]markers|body[_ -]?markers?/i.test(
      error,
    )
  ) {
    return 'An error occurred during rigging.';
  }
  if (
    /image[_ -]to[_ -]3d|mesh generation|geometry generation|initial model|[/\\]generation[/\\]/i.test(
      error,
    )
  ) {
    return 'An error occurred during mesh generation.';
  }
  return 'An error occurred during character generation.';
}

export function applyGeneratedCharacterToJobMetadata(
  job: CharacterJobMetadata,
  item: Pick<
    CharacterGenerationBatchItem,
    | 'uploadStatus'
    | 'characterFbxURL'
    | 'characterFileURL'
    | 'baseGlbURL'
    | 'thumbnailURL'
    | 'referenceImageURL'
    | 'generatedStatus'
    | 'errorMessage'
  >,
): CharacterJobMetadata {
  return {
    ...job,
    uploadStatus: item.uploadStatus,
    generatedStatus: item.generatedStatus,
    errorMessage: item.errorMessage,
    thumbnailURL: item.thumbnailURL,
    referenceImageURL:
      item.referenceImageURL === undefined
        ? job.referenceImageURL
        : item.referenceImageURL,
    characterFileURL: item.characterFileURL,
    characterFbxURL: isCharacterFbxDownloadReady(item.uploadStatus)
      ? (item.characterFbxURL ?? null)
      : null,
    baseGlbURL: item.baseGlbURL ?? job.baseGlbURL,
  };
}
